import { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import SafeArea from "components/safe-area";
import FlatContainer from "components/container/flat";
import Table from "components/table";
import { OutlineButton } from "components/button";
import { Status } from "components/status";
import { CampaignReport } from "../types/ICampaign";
import { getCampaignReport, createFollowUp } from "../api/campaigns";

type StatusFilter = "all" | "sent" | "failed" | "opened" | "not_opened";

const STATUS_COLORS: Record<string, string> = {
  sent: "0,200,100",
  failed: "239,68,68",
  queued: "234,179,8",
  pending: "160,160,160",
};

const CampaignReportPage = () => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.report";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const {
    data: report,
    isLoading,
    isError,
  } = useQuery<CampaignReport>(["campaign-report", id], () =>
    getCampaignReport(id)
  );

  // All useState hooks must be BEFORE any early returns (Rules of Hooks)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const [isFollowingUp, setIsFollowingUp] = useState(false);

  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return "--";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleExportCsv = () => {
    if (!report?.entries) {
      return;
    }

    const headers = ["Recipient", "Status", "Sent At", "Error Message", "First Opened", "Open Count", "First Clicked", "Click Count"];
    const rows = report.entries.map((entry) => [
      entry.recipientAddress,
      entry.status,
      entry.sentAt || "",
      entry.errorMessage || "",
      entry.openedAt || "",
      String(entry.openCount || 0),
      entry.firstClickedAt || "",
      String(entry.clickCount || 0),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `campaign-report-${id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <SafeArea>
        <div className="py-12 flex items-center justify-center">
          <p className="text-gray-600 text-lg">{tr("loading")}</p>
        </div>
      </SafeArea>
    );
  }

  if (isError || !report) {
    return (
      <SafeArea>
        <div className="py-12 flex items-center justify-center">
          <p className="text-red-500 text-lg">{tr("loadError")}</p>
        </div>
      </SafeArea>
    );
  }

  const handleFollowUp = async () => {
    if (statusFilter === "all") return;
    setIsFollowingUp(true);
    try {
      const followUp = await createFollowUp(id, statusFilter);
      history.push(`/outreach/campaigns/${followUp.id}/edit`);
    } catch {
      setIsFollowingUp(false);
    }
  };

  const { campaign, entries, tracking, summary } = report;

  // Client-side filter on the delivery log entries
  const filteredEntries = statusFilter === "all"
    ? entries
    : entries.filter((entry) => {
        switch (statusFilter) {
          case "sent": return entry.status === "sent";
          case "failed": return entry.status === "failed";
          case "opened": return entry.status === "sent" && entry.openedAt != null;
          case "not_opened": return entry.status === "sent" && entry.openedAt == null;
          default: return true;
        }
      });
  // Counted from the send log, the same rows the delivery table below lists.
  // The campaign's own totalSent/totalFailed are frozen when the send finishes,
  // so a delivery result that arrives later — Twilio reporting an undeliverable
  // number — left this header claiming 100% sent above a row marked Failed.
  const totalRecipients = summary.total;
  const totalSent = summary.sent;
  const totalFailed = summary.failed;
  const totalQueued = summary.queued;
  const pctOf = (n: number) =>
    totalRecipients > 0 ? Math.round((n / totalRecipients) * 100) : 0;
  const sentPct = pctOf(totalSent);
  const failedPct = pctOf(totalFailed);
  const queuedPct = pctOf(totalQueued);
  const openPct =
    totalSent > 0
      ? Math.round(((tracking?.uniqueOpens ?? 0) / totalSent) * 100)
      : 0;
  const clickPct =
    totalSent > 0
      ? Math.round(((tracking?.uniqueClicks ?? 0) / totalSent) * 100)
      : 0;

  const logColumns = {
    recipientAddress: {
      label: campaign.campaignType === "sms" ? tr("colPhone") : tr("colEmail"),
      cell: (row: (typeof entries)[0]) => row.recipientAddress,
    },
    status: {
      label: tr("colStatus"),
      cell: (row: (typeof entries)[0]) => (
        <Status color={STATUS_COLORS[row.status] || "160,160,160"}>
          {row.status}
        </Status>
      ),
    },
    sentAt: {
      label: tr("colSentAt"),
      cell: (row: (typeof entries)[0]) => formatDate(row.sentAt),
    },
    errorMessage: {
      label: tr("colError"),
      cell: (row: (typeof entries)[0]) => (
        <span className="text-red-500 text-sm">
          {row.errorMessage || "--"}
        </span>
      ),
    },
    ...(campaign.campaignType === "email"
      ? {
          openedAt: {
            label: tr("colOpenedAt"),
            cell: (row: (typeof entries)[0]) => formatDate(row.openedAt),
          },
        }
      : {}),
    clickCount: {
      label: tr("colClicks"),
      cell: (row: (typeof entries)[0]) => row.clickCount || 0,
    },
  };

  return (
    <SafeArea>
      {/* Header */}
      <FlatContainer
        title={<h2>{tr("title")}</h2>}
        buttons={[
          <OutlineButton
            key="back"
            size="small"
            onClick={() => history.push("/outreach/campaigns")}
          >
            {tr("backToList")}
          </OutlineButton>,
        ]}
      />

      {/* Campaign Details */}
      <div className="py-4 border-b">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <span className="text-sm text-gray-500">{tr("campaignName")}</span>
            <p className="font-semibold">{campaign.name}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">
              {campaign.campaignType === "sms" ? tr("message") : tr("subject")}
            </span>
            <p className="font-semibold">
              {campaign.campaignType === "sms"
                ? (campaign.messageText || "--")
                : (campaign.subject || "--")}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">{tr("sentDate")}</span>
            <p className="font-semibold">{formatDate(campaign.sentAt)}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">{tr("sentBy")}</span>
            <p className="font-semibold">
              {campaign.sentByName || campaign.sentBy || "--"}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="py-4 border-b">
        <h3 className="text-lg font-bold mb-4">{tr("summary")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {/* Total */}
          <div className="text-center">
            <p className="text-3xl font-bold">{totalRecipients}</p>
            <p className="text-sm text-gray-500">{tr("total")}</p>
          </div>
          {/* Sent */}
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-green-700">
                {tr("sent")}
              </span>
              <span className="text-sm font-medium text-green-700">
                {totalSent} ({sentPct}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-green-500 h-3 rounded-full"
                style={{ width: `${sentPct}%` }}
              />
            </div>
          </div>
          {/* Failed */}
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-red-700">
                {tr("failed")}
              </span>
              <span className="text-sm font-medium text-red-700">
                {totalFailed} ({failedPct}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-red-500 h-3 rounded-full"
                style={{ width: `${failedPct}%` }}
              />
            </div>
          </div>
          {/* Queued — email only: sms sends complete in-process, so the
              queued bucket can never be meaningfully non-zero there */}
          {campaign.campaignType === "email" && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-yellow-700">
                  {tr("queued")}
                </span>
                <span className="text-sm font-medium text-yellow-700">
                  {totalQueued > 0 ? totalQueued : 0} ({queuedPct}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-yellow-500 h-3 rounded-full"
                  style={{ width: `${queuedPct}%` }}
                />
              </div>
            </div>
          )}
          {/* Opened (email only) */}
          {campaign.campaignType === "email" && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-blue-700">
                  {tr("opened")}
                </span>
                <span className="text-sm font-medium text-blue-700">
                  {tracking?.uniqueOpens ?? 0} ({openPct}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full"
                  style={{ width: `${openPct}%` }}
                />
              </div>
            </div>
          )}
          {/* Clicked — click tracking only exists when the message carries a
              link (sms links are wrapped in tracking URLs); a linkless sms
              can only ever show 0, so hide the card there */}
          {(campaign.campaignType === "email" ||
            /https?:\/\//i.test(campaign.messageText || "")) && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-purple-700">
                  {tr("clicked")}
                </span>
                <span className="text-sm font-medium text-purple-700">
                  {tracking?.uniqueClicks ?? 0} ({clickPct}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-purple-500 h-3 rounded-full"
                  style={{ width: `${clickPct}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delivery Log */}
      <div className="py-4">
        <FlatContainer
          title={<h3 className="text-lg font-bold">{tr("deliveryLog")}</h3>}
          buttons={[
            statusFilter !== "all" && (
              <OutlineButton
                key="follow-up"
                size="small"
                onClick={handleFollowUp}
                disabled={isFollowingUp || filteredEntries.length === 0}
              >
                {isFollowingUp
                  ? "Creating..."
                  : `Send follow-up to ${filteredEntries.length} recipients`}
              </OutlineButton>
            ),
            <OutlineButton
              key="export"
              size="small"
              onClick={handleExportCsv}
            >
              {tr("exportCsv")}
            </OutlineButton>,
          ].filter(Boolean)}
        >
          {/* Status Filter */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b">
            <label className="text-sm text-gray-500 font-medium">Filter by status:</label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="all">All ({entries.length})</option>
              <option value="sent">Sent ({entries.filter((e) => e.status === "sent").length})</option>
              <option value="failed">Failed ({entries.filter((e) => e.status === "failed").length})</option>
              {campaign.campaignType === "email" && (
                <>
                  <option value="opened">
                    Opened ({entries.filter((e) => e.status === "sent" && e.openedAt != null).length})
                  </option>
                  <option value="not_opened">
                    Not Opened ({entries.filter((e) => e.status === "sent" && e.openedAt == null).length})
                  </option>
                </>
              )}
            </select>
            {statusFilter !== "all" && (
              <span className="text-sm text-gray-400">
                Showing {filteredEntries.length} of {entries.length}
              </span>
            )}
          </div>

          <Table
            data={filteredEntries}
            columns={logColumns}
            disableSortAll
          />
        </FlatContainer>
      </div>
    </SafeArea>
  );
};

export default CampaignReportPage;
